/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <fetchs encadenados por dependencia de datos> */
"use client";

import {
  startTransition,
  useActionState,
  useCallback,
  useEffect,
  useMemo
} from "react";
import { elearningActions } from "@/app/elearning/actions";
import type { FetchParams, RequestResponse } from "@/types";
import type { Course, Enrollment, ProgressView } from "@/types/elearning";

/** Paso siguiente de la cursada, derivado del progreso que devuelve el servidor. */
export interface ModuleNavView {
  /** true mientras la vista de progreso no llegó (no mostrar CTAs de avance). */
  loading: boolean;
  /** Módulo que sigue en la secuencia (null si es el último o mientras carga). */
  nextModule: {
    moduleId: string;
    order: number;
    displayName: string | null;
  } | null;
  /** El módulo actual es el último de la secuencia. */
  esUltimo: boolean;
  /** La inscripción quedó aprobada (el certificado ya se emitió solo, si aplica). */
  cursoAprobado: boolean;
  /** El curso emite certificado (flag `emite_certificado`). */
  issuesCertificate: boolean;
  /** El curso cierra con examen final (completar los módulos no lo aprueba). */
  hasFinalExam: boolean;
  /**
   * A dónde llevar al alumno apenas completa el módulo actual, sin pedirle un
   * segundo clic: el módulo siguiente o, en el último, el certificado (si el curso
   * lo emite y no tiene examen final: aprobar todos los módulos lo emite en la
   * misma escritura) o la página del curso. null mientras el progreso no llegó.
   *
   * No mira `cursoAprobado` a propósito: ese dato viene del progreso cargado al
   * montar y queda viejo justo después de completar el último módulo.
   */
  afterCompleteHref: string | null;
  /** Qué es `afterCompleteHref`, para que el botón de cierre diga a dónde lleva. */
  afterCompleteKind: "nextModule" | "certificate" | "course" | null;
  /** Re-consulta el progreso (p. ej. tras aprobar un quiz) para reflejar el avance. */
  refresh: () => void;
}

/**
 * @function useModuleNav
 * @description Resuelve la navegación de avance dentro de la cursada: dado el módulo
 * actual, qué módulo sigue en la secuencia y si —siendo el último— corresponde
 * ofrecer el certificado. No calcula desbloqueos: solo ordena lo que el servidor
 * devuelve en `GET /enrollments/{id}/progress` (la autorización de acceso al módulo
 * siguiente la sigue arbitrando el backend al navegar). Las banderas del curso se
 * resuelven con la misma cadena que la vista de cursada (inscripción → curso).
 *
 * @param {string} enrollmentId - Inscripción activa.
 * @param {string} moduleId - Módulo actual (referencia para "el siguiente").
 * @returns {ModuleNavView} Navegación derivada + refresh del progreso.
 */
const useModuleNav = (
  enrollmentId: string,
  moduleId: string
): ModuleNavView => {
  const [progressState, fetchProgress] = useActionState<
    RequestResponse<ProgressView> | null,
    FetchParams
  >(elearningActions, null);
  const [enrollmentsState, fetchEnrollments] = useActionState<
    RequestResponse<Enrollment[]> | null,
    FetchParams
  >(elearningActions, null);
  const [courseState, fetchCourse] = useActionState<
    RequestResponse<Course> | null,
    FetchParams
  >(elearningActions, null);

  const refresh = useCallback(() => {
    startTransition(() => {
      fetchProgress({ url: `enrollments/${enrollmentId}/progress` });
    });
  }, [fetchProgress, enrollmentId]);

  useEffect(() => {
    refresh();
    startTransition(() => {
      fetchEnrollments({ url: "enrollments" });
    });
  }, [enrollmentId]);

  /** El curso se resuelve una vez conocida la inscripción (para `emite_certificado`). */
  useEffect(() => {
    const enrollment = enrollmentsState?.data?.find(
      (e) => e.id === enrollmentId
    );
    if (enrollment) {
      startTransition(() => {
        fetchCourse({ url: `courses/${enrollment.courseId}` });
      });
    }
  }, [enrollmentsState]);

  return useMemo(() => {
    const progress =
      progressState?.statusCode === 200 ? progressState.data : null;
    const modules = [...(progress?.modules ?? [])].sort(
      (a, b) => a.order - b.order
    );
    const idx = modules.findIndex((m) => m.moduleId === moduleId);
    const siguiente =
      idx >= 0 && idx < modules.length - 1 ? modules[idx + 1] : null;
    const esUltimo = idx >= 0 && idx === modules.length - 1;
    const issuesCertificate = !!courseState?.data?.issuesCertificate;
    const hasFinalExam = !!courseState?.data?.hasFinalExam;
    const base = `/elearning/mi-aprendizaje/cursos/${enrollmentId}`;

    let afterCompleteHref: string | null = null;
    let afterCompleteKind: ModuleNavView["afterCompleteKind"] = null;
    if (progress && courseState?.data) {
      if (siguiente) {
        afterCompleteHref = `${base}/modulos/${siguiente.moduleId}`;
        afterCompleteKind = "nextModule";
      } else if (esUltimo && issuesCertificate && !hasFinalExam) {
        afterCompleteHref = `${base}/certificado`;
        afterCompleteKind = "certificate";
      } else {
        afterCompleteHref = base;
        afterCompleteKind = "course";
      }
    }

    return {
      loading: !progress,
      nextModule: siguiente
        ? {
            moduleId: siguiente.moduleId,
            order: siguiente.order,
            // El nombre visible viene del curso: el progreso solo trae orden y estado.
            displayName:
              courseState?.data?.modules?.find(
                (m) => m.id === siguiente.moduleId
              )?.displayName ?? null
          }
        : null,
      esUltimo,
      cursoAprobado: progress?.status === "approved",
      issuesCertificate,
      hasFinalExam,
      afterCompleteHref,
      afterCompleteKind,
      refresh
    };
  }, [progressState, courseState, enrollmentId, moduleId, refresh]);
};

export default useModuleNav;
